import { useEffect, useRef, useState } from 'react';
import { UserCircle, ChevronDown, ChevronLeft } from 'lucide-react';
import { Button } from '@reflex/ui/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@reflex/ui/components/ui/popover';
import { PersonaCommandList } from '@reflex/plugin-agent-persona/web/PersonaCommandList';
import { CreatePersonaDialog } from '@reflex/plugin-agent-persona/web/persona-dialogs';
import { emptyPersonaDefaults } from '@reflex/plugin-agent-persona/web/persona-defaults';
import type { AgentPersona } from '@reflex/plugin-agent-persona/shared/types';
import { useAgentTypeRegistry } from '@reflex/ui/client/hooks';
import { useAuth } from '@reflex/ui/client/context';
import { type AgentType } from '@reflex/shared';

export interface LaunchPersonaHeaderProps {
  personas: AgentPersona[];
  selectedPersonaId: string | null;
  detailsOpen: boolean;
  /**
   * Called with the picked persona id, or `null` when the user picks
   * "No persona". Mirrors the previous `string` contract (`'__none__'`
   * meant clear) but uses the typed `AgentPersona | null` shape directly
   * to avoid sentinel-string handling at the call site.
   */
  onPersonaChange: (persona: AgentPersona | null) => void;
  onToggleDetails: () => void;
}

/**
 * Persona picker and details toggle for the Launch dialog header.
 *
 * Renders a Popover-driven search picker (same `Command`-based UI as the
 * global palette's `PersonaPickerDialog` and the `PersonaSearchProvider`,
 * with the active org's `OwnerChip` shown next to each row) plus the
 * chevron toggle button when a persona is selected. When the user has no
 * personas yet the trigger still renders so they can discover the feature;
 * the popover shows a "Create a persona" CTA in that case.
 */
export function LaunchPersonaHeader({
  personas,
  selectedPersonaId,
  detailsOpen,
  onPersonaChange,
  onToggleDetails,
}: LaunchPersonaHeaderProps) {
  const [open, setOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const agentTypeRegistry = useAgentTypeRegistry();
  const defaultAgentType = (agentTypeRegistry.getAll()[0]?.type ?? 'claude-code') as AgentType;
  const { user } = useAuth();
  const blankDefaults = emptyPersonaDefaults(defaultAgentType, user?.id ?? '');
  // Radix Dialog wraps its content in `react-remove-scroll`, which only
  // allows wheel events within its registered shard (the dialog content).
  // Popovers portaled to `document.body` fall outside that shard and their
  // inner scroll containers get locked — the persona list never scrolls.
  // Portal the popover into the dialog content so the cmdk list is covered
  // by the same shard and wheel events reach it again.
  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(null);

  useEffect(() => {
    const host = wrapperRef.current?.closest('[data-slot="dialog-content"]');
    setPortalContainer((host as HTMLElement | null) ?? null);
  }, []);

  const activePersona = selectedPersonaId ? personas.find((p) => p.id === selectedPersonaId) : null;
  const triggerLabel = activePersona?.name ?? 'From persona…';

  function handleSelect(persona: AgentPersona) {
    onPersonaChange(persona);
    setOpen(false);
  }

  function handleClear() {
    onPersonaChange(null);
    setOpen(false);
  }

  function handleCreate() {
    setOpen(false);
    setCreateOpen(true);
  }

  function handleCreated(created: AgentPersona) {
    onPersonaChange(created);
  }

  return (
    <div ref={wrapperRef} className="flex w-full min-w-0 items-center gap-1 sm:w-auto sm:flex-none">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            size="sm"
            role="combobox"
            aria-expanded={open}
            aria-label="Start from persona"
            // Grow to fill the row on mobile (where the picker shares a line
            // with the close button) so the label has room to render; snap
            // back to a fixed width on >=sm so it sits neatly beside the title.
            className="h-7 w-full min-w-0 flex-1 justify-start gap-1.5 px-2 text-xs font-normal sm:w-44 sm:flex-none"
          >
            <UserCircle className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            <span className="truncate" data-placeholder={activePersona ? undefined : 'true'}>
              {triggerLabel}
            </span>
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-auto min-w-[17rem] max-w-[22rem] overflow-y-auto p-0"
          align="end"
          container={portalContainer ?? undefined}
          // Keep the popover within the (overflow-hidden) dialog: bound collision
          // to it and cap the height to the available space so it's never clipped
          // by the modal rim, scrolling internally instead.
          collisionBoundary={portalContainer ?? undefined}
          collisionPadding={8}
          style={{ maxHeight: 'var(--radix-popover-content-available-height)' }}
        >
          <PersonaCommandList
            personas={personas}
            onSelect={handleSelect}
            onClear={activePersona ? handleClear : undefined}
            onCreate={handleCreate}
            maxListHeightClassName="max-h-[280px]"
          />
        </PopoverContent>
      </Popover>
      {activePersona && (
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 text-muted-foreground"
          aria-label={detailsOpen ? 'Hide persona details' : 'Show persona details'}
          onClick={onToggleDetails}
        >
          {detailsOpen ? (
            <ChevronDown className="h-3.5 w-3.5" />
          ) : (
            <ChevronLeft className="h-3.5 w-3.5" />
          )}
        </Button>
      )}
      <CreatePersonaDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={handleCreated}
        metaDefaults={blankDefaults.meta}
        configDefaults={blankDefaults.config}
        title="New agent persona"
        description="Define a reusable persona with prompt, repo, blueprint, and attachments."
        successMessage="Agent persona created"
      />
    </div>
  );
}
